import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Fetch } from '../../utils/Utils.js';
import DetailWhatsAppTip from '../../shared/DetailWhatsAppTip.js';
import { projectTips } from '../../shared/whatsappTips.js';
import EditableText from '../../shared/EditableText.js';
import ErrorMessage from '../../shared/ErrorMessage.js';
import { StatCard, formatLotCode } from '../../shared/lots/LotDetail.js';
import { lotPath, lotPrefixKey } from '../../shared/lots/lotTypes.js';
import LotNavRow from '../../shared/lots/LotNavRow.js';
import FinanceCardList from '../FinanceCardList.js';
import { formatListTitle } from '../../lists/listCode.js';
import { listsPathFor, LISTS_TAB } from '../../lists/listsPath.js';
import ProjectBalanceChart from './ProjectBalanceChart.js';
import { useCurrencySymbol, formatBalance, balanceClass } from '../currency.js';
import '../../weight/lots/WeightLots.css';
import './Projects.css';

// listSummary is a linked list's row summary: "3 of 5 done · R$450.00".
// Done = purchased or completed; the cost is what purchased items added up to.
const listSummary = (items, symbol, t) => {
  if (items.length === 0) return t('lists_no_items');
  const done = items.filter(i => i.status === 'purchased' || i.status === 'completed').length;
  const spent = items
    .filter(i => i.status === 'purchased')
    .reduce((sum, i) => sum + Number(i.cost || 0), 0);
  const summary = t('project_list_summary', { done, total: items.length });
  return spent > 0 ? `${summary} · ${symbol}${spent.toFixed(2)}` : summary;
};

// ProjectDetail — /finances/projects/:projectId (Trello DdqnIHsy): the
// project's balance, its tagged transactions, and editing/closing/deleting.
export const ProjectDetail = () => {
  const { projectId } = useParams();
  const { t }         = useTranslation();
  const navigate      = useNavigate();
  const symbol        = useCurrencySymbol();

  const [project,      setProject]      = useState(null);
  const [missing,      setMissing]      = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [lists,        setLists]        = useState([]);
  const [listItems,    setListItems]    = useState([]);
  const [lots,         setLots]         = useState([]);
  const [confirming,   setConfirming]   = useState(false);
  const [error,        setError]        = useState('');

  const load = useCallback(() => {
    Fetch('api/finance/projects', 'GET', null, (data) => {
      const p = (data || []).find(p => p._id === projectId);
      setProject(p || null);
      setMissing(!p);
    });
    Fetch(`api/data/finance.transactions?search_fields=project_id&search_values=${projectId}`, 'GET', null,
      (data) => setTransactions(data || []));
    // Lists linked to the project (Trello M71keyrQ) and their items, merged
    // in memory by list_id.
    Fetch(`api/data/lists.lists?search_fields=project_id&search_values=${projectId}`, 'GET', null,
      (data) => setLists(data || []), () => {});
    Fetch('api/data/lists.items', 'GET', null, (data) => setListItems(data || []), () => {});
    Fetch(`api/data/lots?search_fields=project_ids&search_values=${projectId}`, 'GET', null,
      (data) => setLots(data || []), () => {});
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const update = (fields) => {
    setError('');
    Fetch(`api/finance/projects/${projectId}`, 'PATCH', JSON.stringify(fields),
      (data) => setProject(data), (message) => setError(message));
  };

  const remove = () => {
    Fetch(`api/finance/projects/${projectId}`, 'DELETE', null,
      () => navigate('/finances/projects'), (message) => setError(message));
  };

  const back = (
    <button className="project-back" onClick={() => navigate('/finances/projects')}>
      ← {t('all_projects')}
    </button>
  );

  if (missing) return <div className="project-empty">{back}<p>{t('project_not_found')}</p></div>;
  if (!project) return <div className="project-empty">{t('loading')}…</div>;


  // Newest first; created_at breaks ties within a day.
  const sortedTransactions = [...transactions].sort((a, b) =>
    (b.date || '').localeCompare(a.date || '') || (b.created_at || '').localeCompare(a.created_at || ''));

  return (
    <div className="wld-page">
      {back}
      <div className="wld-header">
        <div className="wld-header-top">
          <div className="wl-detail-code">{project.code}</div>
          {project.status === 'closed' && <span className="project-status-closed">{t('project_status_closed')}</span>}
        </div>
        <EditableText value={project.name} className="wld-name" inputClassName="wld-name-input"
          placeholder={t('project_name_placeholder')} onSave={(name) => update({ name })} />
        <EditableText value={project.matches} className="wld-matches" inputClassName="wld-matches-input"
          label={t('nicknames')} placeholder={t('project_nicknames_placeholder')}
          onSave={(matches) => update({ matches })} />
      </div>

      {project.status !== 'closed' && (
        <DetailWhatsAppTip title={t('project_tip_title')} tips={projectTips(project, t)} />
      )}

      <div className="wl-stats-grid">
        <StatCard label={t('project_balance')}
          value={<span className={balanceClass(project.balance)}>{formatBalance(project.balance, symbol)}</span>} />
        <StatCard label={t('project_income')} value={`${symbol}${Number(project.gain).toFixed(2)}`} />
        <StatCard label={t('project_expenses')} value={`${symbol}${Number(project.loss).toFixed(2)}`} />
        <StatCard label={t('transactions')} value={project.transaction_count} />
      </div>

      <div style={{ marginTop: 20 }}>
        <ProjectBalanceChart transactions={transactions} currencySymbol={symbol} />
      </div>

      {lists.length > 0 && (
        <div className="project-section" data-testid="project-lists">
          <h3>{t('lists')}</h3>
          <div className="wld-nav-list">
            {lists
              .slice()
              .sort((a, b) => (a.lot_number || 0) - (b.lot_number || 0))
              .map(list => (
                <LotNavRow key={list._id}
                  label={formatListTitle(list, t)}
                  summary={listSummary(listItems.filter(i => i.list_id === list._id), symbol, t)}
                  to={listsPathFor(LISTS_TAB, 'items', list._id)} />
              ))}
          </div>
        </div>
      )}

      {lots.length > 0 && (
        <div className="project-section" data-testid="project-lots">
          <h3>{t('lots')}</h3>
          <div className="wld-nav-list">
            {lots
              .slice()
              .sort((a, b) => (a.lot_id || 0) - (b.lot_id || 0))
              .map(lot => (
                <LotNavRow key={lot._id}
                  label={lot.name
                    ? `${formatLotCode(lot.lot_id, t(lotPrefixKey(lot)))} — ${lot.name}`
                    : formatLotCode(lot.lot_id, t(lotPrefixKey(lot)))}
                  summary={lot.status === 'archived' ? t('lot_status_archived') : null}
                  to={lotPath(lot)} />
              ))}
          </div>
        </div>
      )}

      <div className="project-section" data-testid="project-transactions">
        <h3>{t('transactions')}</h3>
        {transactions.length === 0
          ? <div className="project-empty">{t('no_project_transactions')}</div>
          : <FinanceCardList className="project-finance-cards" data={sortedTransactions} onRefresh={load}
              currencySymbol={symbol} />}
      </div>

      <ErrorMessage message={error} />

      <div className="project-actions">
        {project.status === 'closed'
          ? <button onClick={() => update({ status: 'active' })}>{t('reopen_project')}</button>
          : <button onClick={() => update({ status: 'closed' })}>{t('close_project')}</button>}
        <button className="danger" onClick={() => setConfirming(true)}>{t('delete_project')}</button>
      </div>
      {confirming && (
        <div className="project-confirm">
          {t('delete_project_confirm')}
          <div className="project-confirm-buttons">
            <button className="danger" onClick={remove}>{t('delete_project')}</button>
            <button onClick={() => setConfirming(false)}>{t('cancel')}</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectDetail;
